import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search } from 'app/components/Search'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { DefaultStageMark } from 'app/components/lists/board/DefaultStageMark'
import { ListBoard } from 'app/components/lists/board/ListBoard'
import { ListBoardHeader } from 'app/components/lists/board/ListBoardHeader'
import type { ListBoardLaneDef } from 'app/components/lists/board/boardTypes'
import { ListSlotCard } from 'app/components/lists/board/ListSlotCard'
import { ListKindLine } from 'app/components/lists/ListKindLine'
import { PersonalListMarker } from 'app/components/lists/PersonalListMarker'
import { useListBoard } from 'app/components/lists/board/useListBoard'
import { stageLane } from 'app/components/lists/board/stageLane'
import { useListStageModals } from 'app/components/lists/board/useListStageModals'
import { openListRuleEditor } from 'app/components/lists/filter/openListRuleEditor'
import { useStageVisibility } from 'app/components/lists/manual/useStageVisibility'
import { PipelineLocationSelector } from 'app/components/pipelines/PipelineLocationSelector'
import { contactLocationClause, resolveContactLocationFilter } from 'app/components/pipelines-page/locationFilter'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useListTraversal } from 'app/hooks/useListTraversal'
import { useLocationFilter } from 'app/hooks/useLocationFilter'
import { usePipelineCommContext } from 'app/hooks/usePipelineCommContext'
import { usePipelineTaskCounts } from 'app/hooks/usePipelineTaskCounts'
import { useSettings } from 'app/hooks/useSettings'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { notifyError } from 'app/services/notify.client'
import { useListStore } from 'app/stores/StoreContext'
import type { List } from 'app/generated/graphql'
import type { ContactJson } from 'app/types/entities'
import type { NavListsSetting } from 'app/types/navigation'
import { useHandleSearchParams, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { contactNameTerms } from 'lib/contactSearch'
import { getListCardConfig, LIST_CARD_CONFIG_SETTING, type ListCardConfig } from 'lib/listCardConfig'
import { isFieldEligible } from 'lib/listCardFields'
import { afterListDeletedPath } from 'lib/lists'
import { isPersonalList } from 'lib/listVisibility'
import { logError } from 'lib/logging'
import { navListsSettingKey } from 'lib/navLists'
import { defaultStageIdOf } from 'lib/practiceLists'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'

export function FilterListBoard({
  list,
  viewToggle,
  titleActions,
  onStagesChanged,
}: {
  list: List
  viewToggle?: ReactNode
  titleActions?: ReactNode
  onStagesChanged: () => void
}) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { counts } = useSidebarCounts()
  // Deleting this list from its page lands where deleting it from the sidebar row lands.
  const listStore = useListStore()
  const workspace = useWorkspace()
  const [navLists] = useSettings<NavListsSetting>(navListsSettingKey(workspace?.id))
  const handleSearchParams = useHandleSearchParams()
  const { locationIds, locationOptions, isLoading } = useLocationFilter()
  const locationFilter = useMemo(
    () => resolveContactLocationFilter({ locationIds, locationOptions, isLoading }),
    [locationIds, locationOptions, isLoading],
  )
  const query = searchParams.get(`query`) ?? ``
  const filterTerms = useMemo(
    () => [contactLocationClause(locationFilter), ...contactNameTerms(query)].filter(Boolean),
    [locationFilter, query],
  )

  // Per-user stage visibility and order.
  const { visibleStages } = useStageVisibility(list)
  const stageIds = useMemo(() => (list.stages ?? []).map((stage) => stage.id), [list.stages])
  const hasStages = stageIds.length > 0
  const defaultStageId = defaultStageIdOf(list)

  // The rule is the server's to apply (the list's members are everyone it matches), so the board
  // only adds the location and name narrowing. Without stages there is nothing to draw, so no read.
  const board = useListBoard({
    listId: list.id,
    stageIds,
    filterTerms,
    ready: hasStages && locationFilter.state !== `pending`,
  })

  const onStagesChangedThenReload = () => {
    onStagesChanged()
    board.reload()
  }
  const { openAddStage, openStageEditor } = useListStageModals({ list, onChanged: onStagesChangedThenReload })

  // An edit from the sidebar saves through its own form and lands on this same page, so nothing
  // remounts and the board never hears about the new rule. The first run is the initial read.
  const ruleKey = `${JSON.stringify(list.filters)}|${list.join}`
  const ruleSeen = useRef(ruleKey)
  useEffect(() => {
    if (ruleSeen.current === ruleKey) return
    ruleSeen.current = ruleKey
    board.reload()
  }, [ruleKey])

  // The card's needs. The settings dialog writes this key; the store keeps both readers in step.
  const [rawCardConfig] = useSettings<ListCardConfig>(LIST_CARD_CONFIG_SETTING)
  const cardConfig = getListCardConfig(rawCardConfig, isFieldEligible)

  // The drawer. A lane's row is a list row, so opening a card re-reads the full record. A
  // request id guards a slow read landing after the user opened a different card, and the
  // mounted ref keeps an even later read from setting state on an unmounted board.
  const [drawerContact, setDrawerContact] = useState<ContactJson | undefined>(undefined)
  const openRequestId = useRef(0)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const openContact = async (contactId: string) => {
    if (drawerContact?.id === contactId) return
    const requestId = ++openRequestId.current
    const contact = await gqlFetchOne(`contact`, contactId)
    if (!mounted.current || requestId !== openRequestId.current) return
    if (!contact) {
      logError(`Error opening a contact from a filter list's board`, new Error(`Could not read contact ${contactId}`))
      notifyError(`Couldn't open that contact. Please try again.`)
      return
    }
    setDrawerContact(contact)
  }
  const closeDrawer = () => {
    setDrawerContact(undefined)
    board.reload()
    handleSearchParams({ drawer: undefined })
  }

  // A link into this list carries `?drawer=<id>`. Keyed on the id itself, not
  // `searchParams`, so this fires exactly once per incoming id; `openContact`
  // already no-ops when that id is the one already open.
  const drawerParam = searchParams.get(`drawer`)
  useEffect(() => {
    if (drawerParam && drawerParam !== `new`) bg(openContact(drawerParam))
  }, [drawerParam])

  const openEditList = () =>
    openListRuleEditor({
      list,
      // The page re-reads its lists from the network, rather than reloading the whole app.
      // A new rule changes who is on the board, which a refetched list alone does not reach.
      onSaved: onStagesChangedThenReload,
      onDeleted: () =>
        navigate(
          afterListDeletedPath(
            list.id,
            listStore.hits,
            navLists?.order ?? [],
            searchParams.has(`wId`) ? `?wId=${searchParams.get(`wId`)}` : ``,
          ),
        ),
    })

  const lanes: ListBoardLaneDef<ContactJson, string>[] = visibleStages.map((stage) =>
    stageLane({ id: stage.id!, name: stage.name, color: stage.color }, board, {
      headerAction: (
        <>
          {/* Where new entries, and anyone without a stage yet, land (see `defaultStageIdOf`). */}
          {stage.id === defaultStageId && <DefaultStageMark />}
          <Button
            variant="icon"
            size="icon-sm"
            aria-label={`Edit ${stage.name}`}
            onClick={() => openStageEditor(stage)}
          >
            <PencilSimpleIcon size={14} weight="bold" />
          </Button>
        </>
      ),
    }),
  )

  // The board's own reading list, in lane order: the cards actually on screen are what a card's
  // task counts, comm context and the drawer's next/previous reach for.
  const drawnContacts = lanes.flatMap((lane) => lane.items)
  const contactIds = drawnContacts.map((contact) => contact.id!).filter(Boolean)
  const taskCounts = usePipelineTaskCounts(contactIds, cardConfig.slots.meta.includes(`tasks`))
  const commContext = usePipelineCommContext(drawnContacts, cardConfig.showCommunications)
  const traversal = useListTraversal(drawnContacts, drawerContact, (item) => {
    if (item.id) bg(openContact(item.id))
  })

  // Settled and empty: every read is back, none failed, and the rule matched nobody. Empty
  // lanes would only invite a drag that has nothing to drag.
  const nobodyMatches = !board.loading && !board.error && board.failedLanes.size === 0 && board.total === 0

  return (
    <PageScroll contentClassName="flex h-full flex-col pb-6 pt-8">
      <ListBoardHeader
        title={list.name || `Untitled list`}
        titleMarker={<PersonalListMarker personal={isPersonalList(list)} />}
        subheader={<ListKindLine kind="filter" />}
        totalItems={counts.lists?.[list.id]}
        actions={titleActions}
        headerControls={viewToggle}
        controls={
          <>
            <Search />
            <PipelineLocationSelector />
          </>
        }
        banner={
          board.error && (
            <p className="mt-4 rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-strong">
              {board.error}
            </p>
          )
        }
      />
      {hasStages && nobodyMatches ? (
        <TableEmptyState
          title="Nobody matches this list's rule yet"
          description="People join on their own as they match it. Edit the rule to widen it."
          action={
            <Button variant="secondary" onClick={openEditList}>
              Edit rule
            </Button>
          }
        />
      ) : hasStages ? (
        <ListBoard
          lanes={lanes}
          itemId={(contact) => contact.id ?? undefined}
          touchDrag
          onMove={(contactId, stageId) => bg(board.move(contactId, stageId))}
          renderCard={(contact, drag) => (
            <ListSlotCard
              contact={contact}
              config={cardConfig}
              taskCounts={taskCounts[contact.id!]}
              commContext={commContext[contact.id!]}
              dragging={drag.dragging}
              onDragStart={drag.onDragStart}
              onDragEnd={drag.onDragEnd}
              onOpen={() => bg(openContact(contact.id!))}
            />
          )}
          afterLanes={
            <Button variant="ghost" className="mt-1 shrink-0 self-start" onClick={openAddStage}>
              <PlusIcon size={16} weight="bold" /> Add stage
            </Button>
          }
        />
      ) : (
        <TableEmptyState
          title="This list has no stages yet"
          description="Add stages to work this list as a board. The table view works without them."
          action={<Button onClick={openAddStage}>Add stages</Button>}
        />
      )}
      {/* The board re-reads on close and on change: neither reaches the lanes on its own. */}
      <ContactDrawer
        openDrawer={Boolean(drawerContact)}
        contact={drawerContact}
        onClose={closeDrawer}
        onChange={(next) => {
          setDrawerContact(next)
          board.reload()
        }}
        nextContact={traversal.next}
        previousContact={traversal.prev}
        isNavigating={traversal.isNavigating}
      />
    </PageScroll>
  )
}
